/**
 * IndexedDB 초기화 및 관리
 * 오프라인 데이터 캐싱을 위한 로컬 데이터베이스
 */

import { DB_NAME, DB_VERSION } from "./constants";

// Object Store 이름
export const STORES = {
  PROJECTS: "projects",
  MACHINES: "machines",
  SYNC_QUEUE: "syncQueue",
  PHOTOS: "photos",
  SYNC_META: "syncMeta",
  /** Feature 288: 설비 템플릿 캐시 (오프라인 설비 생성용) */
  MACHINE_TEMPLATES: "machineTemplates",
  /** Feature 326: ID 매핑 (오프라인 → 서버 ID 변환) */
  ID_MAPPING: "idMapping",
} as const;

let dbInstance: IDBDatabase | null = null;

/**
 * IndexedDB 연결 및 초기화
 */
export async function openDB(): Promise<IDBDatabase> {
  if (dbInstance) {
    return dbInstance;
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // projects store - 캐시된 프로젝트 데이터
      if (!db.objectStoreNames.contains(STORES.PROJECTS)) {
        const projectStore = db.createObjectStore(STORES.PROJECTS, {
          keyPath: "_id",
        });
        projectStore.createIndex("by-synced", "synced");
        projectStore.createIndex("by-cachedAt", "cachedAt");
      }

      // machines store - 캐시된 설비 데이터
      if (!db.objectStoreNames.contains(STORES.MACHINES)) {
        const machineStore = db.createObjectStore(STORES.MACHINES, {
          keyPath: "_id",
        });
        machineStore.createIndex("by-projectId", "projectId");
        machineStore.createIndex("by-synced", "synced");
      }

      // syncQueue store - 동기화 대기열
      if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, {
          keyPath: "id",
          autoIncrement: true,
        });
        syncStore.createIndex("by-status", "status");
        syncStore.createIndex("by-entityType", "entityType");
        syncStore.createIndex("by-createdAt", "createdAt");
      }

      // photos store - 로컬 사진 데이터
      if (!db.objectStoreNames.contains(STORES.PHOTOS)) {
        const photoStore = db.createObjectStore(STORES.PHOTOS, {
          keyPath: "id",
        });
        photoStore.createIndex("by-machineId", "machineId");
        photoStore.createIndex("by-synced", "synced");
      }

      // syncMeta store - 동기화 메타데이터
      if (!db.objectStoreNames.contains(STORES.SYNC_META)) {
        db.createObjectStore(STORES.SYNC_META, {
          keyPath: "key",
        });
      }

      // machineTemplates store - 설비 템플릿 캐시 (Feature 288)
      if (!db.objectStoreNames.contains(STORES.MACHINE_TEMPLATES)) {
        const templateStore = db.createObjectStore(STORES.MACHINE_TEMPLATES, {
          keyPath: "key",
        });
        templateStore.createIndex("by-cachedAt", "cachedAt");
      }

      // idMapping store - ID 매핑 (Feature 326)
      if (!db.objectStoreNames.contains(STORES.ID_MAPPING)) {
        const mappingStore = db.createObjectStore(STORES.ID_MAPPING, {
          keyPath: "localId",
        });
        mappingStore.createIndex("by-serverId", "serverId");
        mappingStore.createIndex("by-entityType", "entityType");
      }
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
}

/**
 * 데이터베이스 연결 종료
 */
export function closeDB(): void {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
}

/**
 * 데이터베이스 삭제 (테스트/디버깅용)
 */
export async function deleteDB(): Promise<void> {
  closeDB();
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * 트랜잭션 헬퍼
 */
export async function withTransaction<T>(
  storeNames: string | string[],
  mode: IDBTransactionMode,
  callback: (tx: IDBTransaction) => Promise<T>
): Promise<T> {
  const db = await openDB();
  const tx = db.transaction(storeNames, mode);
  return callback(tx);
}

/**
 * 단일 레코드 조회
 */
export async function getRecord<T>(storeName: string, key: IDBValidKey): Promise<T | undefined> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const store = tx.objectStore(storeName);
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result as T | undefined);
    request.onerror = () => reject(request.error);
  });
}

/**
 * 레코드 저장 (upsert)
 */
export async function putRecord<T>(storeName: string, record: T): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);
    const request = store.put(record);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * 레코드 삭제
 */
export async function deleteRecord(storeName: string, key: IDBValidKey): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);
    const request = store.delete(key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * 모든 레코드 조회
 */
export async function getAllRecords<T>(storeName: string): Promise<T[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const store = tx.objectStore(storeName);
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result as T[]);
    request.onerror = () => reject(request.error);
  });
}

/**
 * 인덱스로 레코드 조회
 */
export async function getRecordsByIndex<T>(
  storeName: string,
  indexName: string,
  key: IDBValidKey
): Promise<T[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const store = tx.objectStore(storeName);
    const index = store.index(indexName);
    const request = index.getAll(key);
    request.onsuccess = () => resolve(request.result as T[]);
    request.onerror = () => reject(request.error);
  });
}

/**
 * 스토어 클리어 (모든 레코드 삭제)
 */
export async function clearStore(storeName: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);
    const request = store.clear();
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * 인덱스에서 고유 키 목록 조회 (커서 사용, 메모리 효율적)
 * 전체 레코드를 로드하지 않고 인덱스 키만 순회
 */
export async function getUniqueIndexKeys(
  storeName: string,
  indexName: string
): Promise<Set<string>> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const store = tx.objectStore(storeName);
    const index = store.index(indexName);
    const keys = new Set<string>();

    // openKeyCursor: 키만 조회 (값 로드 안함)
    const request = index.openKeyCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        keys.add(String(cursor.key));
        cursor.continue();
      } else {
        resolve(keys);
      }
    };
    request.onerror = () => reject(request.error);
  });
}
